import type { FC } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { LoadingAnimation } from "./LoadingAnimation.tsx";

const LoadingScreenContext = createContext({
  show: () => {
    try {
      throw new Error("LoadingScreenContext.show called outside a provider");
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.name, error.message, error.stack);
      }
    }
  },
  hide: () => {
    try {
      throw new Error("LoadingScreenContext.hide called outside a provider");
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.name, error.message, error.stack);
      }
    }
  },
  visible: false,
});

export const LoadingScreenProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
  ...props
}) => {
  const [pending, setPending] = useState(0);

  const show = useCallback(() => setPending((current) => current + 1), []);
  const hide = useCallback(() => setPending((current) => current - 1), []);

  const visible = pending > 0;

  const context = useMemo(
    () => ({ show, hide, visible }),
    [show, hide, visible],
  );

  return (
    <LoadingScreenContext.Provider value={context} {...props}>
      <LoadingAnimation visible={visible} />
      {children}
    </LoadingScreenContext.Provider>
  );
};

export function useLoadingScreenContext() {
  return useContext(LoadingScreenContext);
}

export function useIsLoadingScreenVisible() {
  return useContext(LoadingScreenContext).visible;
}
